'use client';

import React from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Drawer } from 'vaul';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

// Define the Service interface here or import it if shared
interface Service {
    id: number;
    title: string;
    description: string;
    icon: string;
    features: string[];
    link: string;
    image: string;
    gradient: string;
    accent: string;
    bg: string;
}

interface MobileServicesOverviewProps {
    services: Service[];
}

const MobileServicesOverview: React.FC<MobileServicesOverviewProps> = ({ services }) => {
    const [emblaRef] = useEmblaCarousel({
        loop: false,
        align: 'center',
        containScroll: 'trimSnaps'
    });

    return (
        <div className="py-12 bg-slate-50">
            <div className="px-6 mb-8 text-center">
                <h2 className="text-3xl font-black text-slate-900 tracking-tight mb-3">
                    Complete Digital <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-violet-600">Ecosystems</span>
                </h2>
                <p className="text-sm text-slate-600 font-medium">
                    Swipe to explore our core pillars.
                </p>
            </div>

            {/* Carousel Container */}
            <div className="overflow-hidden" ref={emblaRef}>
                <div className="flex touch-pan-y pl-6 pr-12">
                    {services.map((service) => (
                        <div key={service.id} className="flex-[0_0_85%] min-w-0 pr-4">
                            <Drawer.Root>
                                <Drawer.Trigger asChild>
                                    <div className={`
                                        relative overflow-hidden rounded-[2rem] p-5 h-[420px] 
                                        flex flex-col justify-between 
                                        transition-transform active:scale-95 shadow-xl shadow-slate-200/50
                                        bg-white border border-slate-100
                                    `}>
                                        {/* Background Gradient */}
                                        <div className={`absolute top-0 right-0 w-48 h-48 bg-gradient-to-br ${service.gradient} opacity-10 rounded-full blur-3xl -mr-12 -mt-12`} />
                                        <div className={`absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr ${service.gradient} opacity-10 rounded-full blur-2xl -ml-8 -mb-8`} />

                                        {/* Header */}
                                        <div>
                                            <div className="flex items-start justify-between mb-4">
                                                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white shadow-lg`}>
                                                    <Icon name={service.icon} className="w-6 h-6" />
                                                </div>
                                            </div>

                                            <h3 className="text-2xl font-bold text-slate-900 leading-tight mb-3">
                                                {service.title}
                                            </h3>
                                            <p className="text-slate-600 line-clamp-[6] leading-relaxed text-sm">
                                                {service.description}
                                            </p>
                                        </div>

                                        {/* Footer */}
                                        <div className="mt-6">
                                            <div className={`inline-flex items-center text-sm font-bold ${service.accent}`}>
                                                View Details
                                                <div className={`ml-2 w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center`}>
                                                    <Icon name="ArrowRightIcon" className="w-4 h-4" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Drawer.Trigger>
                                <Drawer.Portal>
                                    <Drawer.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" />
                                    <Drawer.Content className="bg-white flex flex-col rounded-t-[32px] h-[85vh] mt-24 fixed bottom-0 left-0 right-0 z-50 outline-none">
                                        {/* Handle */}
                                        <div className="p-4 bg-white rounded-t-[32px] flex-shrink-0">
                                            <div className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-slate-200 mb-2" />
                                        </div>

                                        <div className="p-6 pb-24 overflow-y-auto flex-1">
                                            {/* Drawer Header */}
                                            <div className="mb-8">
                                                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white shadow-lg mb-4`}>
                                                    <Icon name={service.icon} className="w-7 h-7" />
                                                </div>
                                                <Drawer.Title className="text-2xl font-bold text-slate-900 mb-2">
                                                    {service.title}
                                                </Drawer.Title>
                                                <Drawer.Description className="text-slate-600 leading-relaxed">
                                                    {service.description}
                                                </Drawer.Description>
                                            </div>

                                            {/* Features List */}
                                            <div className="space-y-4 mb-8">
                                                <h4 className="font-bold text-slate-900 text-sm uppercase tracking-wider">Key Features</h4>
                                                <div className="grid grid-cols-1 gap-2">
                                                    {service.features.map((feature, i) => (
                                                        <div key={i} className="flex items-center p-3 rounded-xl bg-slate-50 border border-slate-100">
                                                            <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${service.gradient} mr-3`} />
                                                            <span className="text-slate-700 font-medium">{feature}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* CTA replaced with Close to keep user on homepage */}
                                            <Drawer.Close asChild>
                                                <button className={`w-full py-4 rounded-xl font-bold bg-slate-100 text-slate-900 hover:bg-slate-200 transition-colors flex items-center justify-center`}>
                                                    Close & Continue Exploring
                                                </button>
                                            </Drawer.Close>
                                        </div>
                                    </Drawer.Content>
                                </Drawer.Portal>
                            </Drawer.Root>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default MobileServicesOverview;
